export {
  ZOOM_INITIAL,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_STEP,
} from '@blocksuite/std/gfx';
export const GRID_SIZE = 3000;
export const GRID_GAP_MIN = 5;
export const GRID_GAP_MAX = 80;
export const DEFAULT_NOTE_OFFSET_X = 30;
export const DEFAULT_NOTE_OFFSET_Y = 40;

// ---- Shared canvas background (edgeless + pixi workflow viewer) ----

/** Base world-space gap between grid dots (px) */
export const GRID_WORLD_GAP = 20;

/** Minimum screen-space gap before LOD doubles the world gap */
export const GRID_MIN_SCREEN_GAP = 10;

/** Subtle top-center highlight for dark canvas backgrounds. */
export const DARK_CANVAS_SPOTLIGHT_GRADIENT =
  'radial-gradient(ellipse 100% 60% at 50% 0%, rgba(255,255,255,0.04) 0%, transparent 50%)';

/** Default for renderers whose surfaces are always dark. */
export const CANVAS_SPOTLIGHT_GRADIENT = DARK_CANVAS_SPOTLIGHT_GRADIENT;

/** Subtle warm top-center shading for light canvas backgrounds. */
export const LIGHT_CANVAS_SPOTLIGHT_GRADIENT =
  'radial-gradient(ellipse 100% 60% at 50% 0%, rgba(80,60,40,0.04) 0%, transparent 50%)';

export type EdgelessGridStyle = 'dots' | 'crosses' | 'lines';

export const DEFAULT_EDGELESS_GRID_STYLE: EdgelessGridStyle = 'lines';
export const EDGELESS_GRID_STYLE_ATTRIBUTE = 'data-streamlain-grid-style';
export const EDGELESS_GRID_STYLE_CHANGED_EVENT =
  'streamlain:edgeless-grid-style-changed';
export const EDGELESS_GRID_BACKGROUND_IMAGE_VAR =
  '--streamlain-edgeless-grid-background-image';

export function normalizeEdgelessGridStyle(value: unknown): EdgelessGridStyle {
  return value === 'dots' || value === 'crosses' || value === 'lines'
    ? value
    : DEFAULT_EDGELESS_GRID_STYLE;
}

export function createEdgelessGridBackgroundImage(
  style: EdgelessGridStyle,
  color: string
): string | null {
  // Lines need viewport-sized, device-pixel-aligned geometry (no tiled segments).
  // Dots retain their CSS radial-gradient background.
  if (style !== 'crosses') return null;

  const stroke = color.trim() || 'rgba(127, 127, 127, 0.35)';
  const mark = `<path d="M7 10h6M10 7v6" fill="none" stroke="${stroke}" stroke-width="0.5" vector-effect="non-scaling-stroke"/>`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">${mark}</svg>`;

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/**
 * Calculate screen-space grid gap with LOD.
 * Linear scaling with min-only clamp (prevents dot flicker on zoom-out).
 */
export function calculateGridScreenGap(zoom: number): number {
  let worldGap = GRID_WORLD_GAP;
  while (worldGap * zoom < GRID_MIN_SCREEN_GAP) worldGap *= 2;
  return worldGap * zoom;
}

// TODO: need to check the default central area ratio
export const DEFAULT_CENTRAL_AREA_RATIO = 0.3;

export interface IModelCoord {
  x: number;
  y: number;
}

// TODO(@L-Sun): we should remove this list when refactor the pointerOut event to pointerLeave,
// since the previous will be triggered when the pointer move to the area of the its children elements
// see: https://developer.mozilla.org/en-US/docs/Web/API/Element/pointerout_event
export const EXCLUDING_MOUSE_OUT_CLASS_LIST = [
  'affine-note-mask',
  'edgeless-block-portal-note',
  'affine-block-children-container',
  'edgeless-container',
];
